import { useState, useEffect, useCallback } from 'react';
import logger from '../shared/utils/logger';
import { getErrorMessage } from '../shared/utils/errorMessage';
import { App as CapacitorApp } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Capacitor } from '@capacitor/core';
import updateService from '../services/update-service';
import { toast } from 'react-toastify';
import packageJson from '../../package.json';

// Constants
const UPDATE_CHECK_THROTTLE_MS = 3 * 60 * 1000; // 3 minutes
const DOWNLOAD_TIMEOUT_MS = 30000; // 30 seconds
const APP_ID = 'com.applepie.app';
const DEFAULT_VERSION = '1.0.0';
const STORAGE_KEYS = {
  lastCheckTime: 'lastUpdateCheckTime',
  lastSuccessfulUpdate: 'lastSuccessfulUpdateTime',
  token: 'token',
};

// Safe localStorage wrappers (can throw in private browsing / restricted environments)
const safeGetItem = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSetItem = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
};
const safeRemoveItem = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
};

// Types
interface UpdateCheckResponse {
  updateAvailable?: boolean;
  latestVersion?: string;
  updateUrl?: string;
  isMandatory?: boolean;
  releaseNotes?: string;
  version?: string;
  mandatory?: boolean;
}

interface AppInfo {
  version: string;
  platform: string;
  appId: string;
}

// Removed unused interface

/**
 * Hook to handle app updates checking on login
 */
export const useAppUpdater = () => {
  // State
  const [appInfo, setAppInfo] = useState<AppInfo | null>(null);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [hasCheckedThisSession, setHasCheckedThisSession] = useState(false);

  /**
   * Utility function to log with consistent prefix
   */
  const log = useCallback((message: string, data?: unknown) => {
    if (data) {
      logger.log(`[AppUpdater] ${message}`, data);
    } else {
      logger.log(`[AppUpdater] ${message}`);
    }
  }, []);

  /**
   * Utility function to log errors with consistent prefix
   */
  const logError = useCallback((message: string, error?: unknown) => {
    if (error) {
      logger.error(`[AppUpdater] ${message}`, error);
    } else {
      logger.error(`[AppUpdater] ${message}`);
    }
  }, []);

  /**
   * Clear update throttling timers (for debugging)
   */
  const clearUpdateThrottling = useCallback(() => {
    safeRemoveItem(STORAGE_KEYS.lastCheckTime);
    safeRemoveItem(STORAGE_KEYS.lastSuccessfulUpdate);
    setHasCheckedThisSession(false);
    log('Cleared update throttling timers');
  }, [log]);

  /**
   * Record a successful update in localStorage
   */
  const recordSuccessfulUpdate = useCallback(() => {
    safeSetItem(STORAGE_KEYS.lastSuccessfulUpdate, Date.now().toString());
    log('Recorded successful update time in localStorage');
  }, [log]);

  /**
   * Check if an update check should be throttled
   */
  const shouldThrottleUpdateCheck = useCallback(() => {
    const lastCheckTime = safeGetItem(STORAGE_KEYS.lastCheckTime);
    const lastSuccessfulUpdate = safeGetItem(STORAGE_KEYS.lastSuccessfulUpdate);
    const now = Date.now();
    
    log('Time-based checks:', {
      now: new Date(now).toLocaleString(),
      lastCheckTime: lastCheckTime ? new Date(parseInt(lastCheckTime)).toLocaleString() : 'never',
      lastSuccessfulUpdate: lastSuccessfulUpdate ? new Date(parseInt(lastSuccessfulUpdate)).toLocaleString() : 'never'
    });
    
    // If we've checked in the last throttle period, skip
    if (lastCheckTime && (now - parseInt(lastCheckTime)) < UPDATE_CHECK_THROTTLE_MS) {
      const timeSinceLastCheck = Math.round((now - parseInt(lastCheckTime)) / 1000);
      log(`Last update check was ${timeSinceLastCheck} seconds ago (< 3 minutes), skipping`);
      return true;
    }
    
    // If we've successfully updated in the last throttle period, skip
    if (lastSuccessfulUpdate && (now - parseInt(lastSuccessfulUpdate)) < UPDATE_CHECK_THROTTLE_MS) {
      const timeSinceLastUpdate = Math.round((now - parseInt(lastSuccessfulUpdate)) / 1000);
      log(`Successfully updated ${timeSinceLastUpdate} seconds ago (< 3 minutes), skipping`);
      return true;
    }
    
    return false;
  }, [log]);

  /**
   * Get the current bundle info from CapacitorUpdater
   */
  const getCurrentBundle = useCallback(async () => {
    try {
      const currentBundle = await CapacitorUpdater.current();
      log('Current bundle info:', JSON.stringify(currentBundle));
      return currentBundle;
    } catch (error: unknown) {
      logError('Error getting current bundle info:', error);
      return null;
    }
  }, [log, logError]);

  /**
   * Apply an update with the given bundle ID
   */
  const applyUpdate = useCallback(async (
    downloadId: string, 
    version: string, 
    isMandatory: boolean
  ) => {
    if (!downloadId) {
      logError('No download ID available, cannot set update');
      toast.error('Update preparation failed. Please try again later.');
      return;
    }
    
    // Give the system a moment to finish processing the downloaded bundle
    log('Waiting 2 seconds before setting the update...');
    await new Promise(resolve => setTimeout(resolve, 2000));

    const applyUpdateInternal = async () => {
      try {
        log(`Setting update with ID: ${downloadId}`);
        log('Directly setting update without reset');
        
        // Need to use 'as any' since the type definitions don't include version
        const setResult = await CapacitorUpdater.set({
          id: downloadId,
          version: version, // Include version explicitly
          next: true,       // Set as next bundle to use
          immediate: false  // Don't apply immediately to avoid reset issues
        } as any);
        
        log('Set completed successfully:', JSON.stringify(setResult));
        log('Reloading app to apply update');
        
        // Record successful update time in localStorage
        recordSuccessfulUpdate();
        
        // Add a delay before reloading to ensure the update is properly set
        log('Waiting 1 second before reloading to apply update...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Check current bundle before reload
        try {
          const currentBeforeReload = await CapacitorUpdater.current();
          log('Current bundle before reload:', JSON.stringify(currentBeforeReload));
        } catch (e: unknown) {
          logError('Error getting current bundle before reload:', e);
        }
        
        // Force restart the app to apply the update
        log('Forcing app restart to apply update');
        if (Capacitor.isNativePlatform()) {
          // For mobile platforms, use CapacitorUpdater.reset() to force a complete restart
          await CapacitorApp.exitApp();
        } else {
          // Fallback to reload for web platform
          window.location.reload();
        }
      } catch (setError: unknown) {
        logError('Set failed:', setError);
        toast.error(getErrorMessage(setError, 'Failed to apply update. Please restart the app.'));
      }
    };

    if (isMandatory) {
      log('Applying mandatory update immediately');
      await applyUpdateInternal();
    } else {
      log('Showing optional update notification');
      // For optional updates, notify the user
      toast.info(
        'An update is available. Install now?',
        {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          closeButton: false,
          position: 'bottom-center',
          onClick: applyUpdateInternal
        }
      );
    }
  }, [log, logError, recordSuccessfulUpdate]);

  /**
   * Download an update package
   */
  const downloadUpdate = useCallback(async (
    updateUrl: string, 
    version: string
  ) => {
    // Get the authentication token
    const token = safeGetItem(STORAGE_KEYS.token);
    if (!token) {
      logError('No authentication token found for download');
      toast.error('Authentication required for updates');
      return null;
    }
    
    let downloadId: string | null = null;
    let downloadComplete = false;
    
    // Set up download listeners before creating the promise
    let downloadFinishedListener: { remove: () => void } | null = null;
    let downloadFailedListener: { remove: () => void } | null = null;
    
    // Create promise for download completion
    const downloadCompletedPromise = new Promise<string>(async (resolve, reject) => {
      try {
        // Capacitor Updater plugin exposes these events at runtime but its TypeScript
        // definitions don't include them — cast event names to satisfy the compiler.
        type PluginListener = { remove: () => void };
        const addListener = CapacitorUpdater.addListener.bind(CapacitorUpdater) as unknown as (
          event: string, cb: (info: Record<string, unknown>) => void
        ) => Promise<PluginListener>;

        // Add listener for download completion
        const completeListener = await addListener('downloadComplete', (info) => {
          log('Download complete event:', JSON.stringify(info));
          downloadComplete = true;

          // Handle different response formats
          if (info && typeof info === 'object') {
            if ('id' in info) {
              resolve(info.id as string);
            } else if ('bundle' in info && typeof info.bundle === 'object' && info.bundle !== null && 'id' in info.bundle) {
              resolve((info.bundle as Record<string, unknown>).id as string);
            } else {
              log('No bundle ID in response, using version as fallback');
              const fallbackId = `manual-${version}-${Date.now()}`;
              resolve(fallbackId);
            }
          } else {
            log('Invalid download complete info, using emergency fallback ID');
            const emergencyId = `emergency-${Date.now()}`;
            resolve(emergencyId);
          }
        });
        downloadFinishedListener = completeListener;

        // Add listener for download failure
        const failedListener = await addListener('downloadFailed', (info) => {
          logError('Download failed event:', JSON.stringify(info));
          reject(new Error('Download failed: ' + JSON.stringify(info)));
        });
        downloadFailedListener = failedListener;

        // Also listen for updateFailed events
        await addListener('updateFailed', (info) => {
          logError('Update failed event:', JSON.stringify(info));
          reject(new Error('Update failed: ' + JSON.stringify(info)));
        });
        
        // Set a timeout in case the events never fire
        setTimeout(() => {
          if (!downloadComplete) {
            log('Download timeout after 30 seconds');
            reject(new Error('Download timeout after 30 seconds'));
          }
        }, DOWNLOAD_TIMEOUT_MS);
      } catch (error: unknown) {
        logError('Error setting up download listeners:', error);
        reject(error);
      }
    });
    
    // Add cleanup function to run after promise resolves or rejects
    downloadCompletedPromise.finally(() => {
      log('Cleaning up download listeners');
      try {
        if (downloadFinishedListener && typeof downloadFinishedListener.remove === 'function') {
          downloadFinishedListener.remove();
        }
        if (downloadFailedListener && typeof downloadFailedListener.remove === 'function') {
          downloadFailedListener.remove();
        }
      } catch (error: unknown) {
        logError('Error cleaning up listeners:', error);
      }
    });
    
    try {
      // Register event listeners for debugging
      CapacitorUpdater.addListener('download', (info) => {
        log('Download progress:', JSON.stringify(info));
      });
      
      // Start the download with authentication header
      log('Starting download from:', updateUrl);
      
      // Get current bundle for comparison
      const currentBundle = await getCurrentBundle();
      
      // Clear any existing bundles first
      log('Getting list of available bundles...');
      const bundles = await CapacitorUpdater.list();
      log('Available bundles:', JSON.stringify(bundles));
      
      // Delete all existing bundles to avoid conflicts
      if (bundles && Array.isArray(bundles.bundles)) {
        for (const bundle of bundles.bundles) {
          try {
            // Don't delete the currently active bundle
            if (bundle.id === currentBundle?.bundle?.id) {
              log(`Skipping deletion of active bundle: ${bundle.id}, version: ${bundle.version}`);
              continue;
            }
            log(`Deleting bundle: ${bundle.id}, version: ${bundle.version}`);
            await CapacitorUpdater.delete({ id: bundle.id });
          } catch (e: unknown) {
            log(`Error deleting bundle ${bundle.id}:`, e);
          }
        }
      }
      
      log('Starting download with version:', version);
      
      // Use the update URL directly from the backend, just ensure it ends with .zip
      const downloadUrl = updateUrl.endsWith('.zip') ? updateUrl : `${updateUrl}.zip`;

      log('Starting download from URL:', downloadUrl);

      // Pass the auth token as a request header, not as a URL query parameter.
      // Tokens in URLs are logged in browser history, server access logs, and CDN/proxy logs.
      await CapacitorUpdater.download({
        url: downloadUrl,
        version: version,
        headers: { Authorization: `Bearer ${token}` },
        checksum: undefined // Let the plugin handle checksum automatically
      } as any);
      
      // Wait for the download to complete via the event
      log('Waiting for download to complete...');
      downloadId = await downloadCompletedPromise;
      log(`Download confirmed complete with ID: ${downloadId}`);
      
      // Add a delay to ensure the file is properly processed before attempting to unzip
      log('Adding delay to ensure file is ready for processing...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return downloadId;
    } catch (downloadError: unknown) {
      logError('Direct download failed:', downloadError);
      return null;
    }
  }, [log, logError, getCurrentBundle]);

  /**
   * Handle mobile updates with Capacitor Updater
   */
  const handleMobileUpdate = useCallback(async (
    updateUrl: string, 
    isMandatory: boolean, 
    version: string
  ) => {
    log(`Starting mobile update process with URL: ${updateUrl}, mandatory: ${isMandatory}, version: ${version}`);
    
    try {
      // Check if we're already running the latest version - DO THIS FIRST
      const currentBundle = await getCurrentBundle();
      
      if (currentBundle?.bundle?.version === version) {
        log(`Already running the latest version (${version}). Skipping update.`);
        // Also set hasCheckedThisSession to prevent further checks this session
        setHasCheckedThisSession(true);
        return;
      }
      
      // Use the EXACT URL provided by the API
      log(`Original update URL from API: ${updateUrl}`);
      
      // Download the update
      const downloadId = await downloadUpdate(updateUrl, version);
      
      // Apply the update if download was successful
      if (downloadId) {
        await applyUpdate(downloadId, version, isMandatory);
      }
    } catch (error: unknown) {
      logError('Error installing update:', error);
      logError('Error details:', JSON.stringify(error));
      toast.error(getErrorMessage(error, 'Failed to install update. Please try again later.'));
    }
  }, [log, logError, getCurrentBundle, downloadUpdate, applyUpdate]);

  /**
   * Check for updates - this should be called once after login
   */
  const checkForUpdates = useCallback(async () => {
    // Prevent multiple checks in the same session
    if (hasCheckedThisSession) {
      log('Already checked for updates this session, skipping');
      return;
    }
    
    // Check if we should throttle based on recent checks/updates
    if (shouldThrottleUpdateCheck()) {
      return;
    }
    
    // Store the current time as the last check time
    safeSetItem(STORAGE_KEYS.lastCheckTime, Date.now().toString());
    log('Stored current time as last check time:', new Date(Date.now()).toLocaleString());

    log('Starting update check...');
    
    // Check if user is authenticated by checking for token
    const token = safeGetItem(STORAGE_KEYS.token);
    if (!token) {
      log('No authentication token found, skipping update check');
      return;
    }
    
    // Get current app info directly from state or use fallback
    // DEFAULT_VERSION
    let currentAppInfo = appInfo || {
      version: packageJson.version || '999',
      platform: Capacitor.getPlatform() === 'android' ? 'android' : 'web',
      appId: APP_ID
    };
    
    // For mobile platforms, check if we're already running from an updated bundle
    if (Capacitor.isNativePlatform()) {
      try {
        const currentBundle = await getCurrentBundle();

        // If we're already running a non-builtin bundle, use its version for the update check
        if (currentBundle?.bundle?.version && 
            currentBundle.bundle.version !== 'builtin' && 
            currentBundle.bundle.status === 'success') {
          log(`Running from bundle version ${currentBundle.bundle.version}`);
          
          // Create new app info object with bundle version
          currentAppInfo = {
            ...currentAppInfo,
            version: currentBundle.bundle.version
          };
          log(`Updated app info to use bundle version ${currentBundle.bundle.version} for update check`);
        }
      } catch (e: unknown) {
        log('Error checking current bundle:', e);
      }
    }
    
    log('Using app info for update check:', JSON.stringify(currentAppInfo));
    
    if (isCheckingUpdate) {
      log('Update check already in progress, skipping');
      return;
    }
    
    // Add timestamp to log to track frequency
    log(`Update check initiated at: ${new Date().toLocaleString()}`);
    
    setIsCheckingUpdate(true);
    try {
      const { appId, version, platform } = currentAppInfo;
      log(`Checking for updates with appId=${appId}, version=${version}, platform=${platform}`);
      
      const result = await updateService.checkForUpdates(appId, version, platform) as unknown as UpdateCheckResponse;
      log('Update check response:', JSON.stringify(result));
      
      // Mark that we've checked this session
      setHasCheckedThisSession(true);
      
      // Handle both property naming conventions (updateAvailable or from version comparison)
      const hasUpdate = result?.updateAvailable || false;
      log(`Update available: ${hasUpdate}`);
      setUpdateAvailable(hasUpdate);
      
      if (hasUpdate) {
        // Get update URL (handle both property naming conventions)
        const updateUrl = result?.updateUrl || '';
        // Get mandatory flag (handle both property naming conventions)
        const isMandatory = result?.isMandatory || result?.mandatory || false;
        // Get latest version (handle both property naming conventions)
        const latestVersion = result?.latestVersion || result?.version || 'new version';
        
        log(`Update details - URL: ${updateUrl}, mandatory: ${isMandatory}, version: ${latestVersion}`);
        
        // Check if we're already running this version from a bundle
        if (Capacitor.isNativePlatform()) {
          try {
            const currentBundle = await getCurrentBundle();
            
            const currentBundleVersion = currentBundle?.bundle?.version;
            const currentBundleStatus = currentBundle?.bundle?.status;
            
            log('Version comparison:', {
              currentBundleVersion,
              latestVersion,
              currentBundleStatus,
              versionsMatch: currentBundleVersion === latestVersion,
              statusIsSuccess: currentBundleStatus === 'success'
            });
            
            if (currentBundleVersion === latestVersion && currentBundleStatus === 'success') {
              log(`Already running bundle version ${latestVersion} with success status. Skipping update.`);
              return;
            }
          } catch (e: unknown) {
            log('Error checking current bundle:', e);
          }
        }
        
        if (Capacitor.isNativePlatform()) {
          log('Detected mobile platform, using Capacitor Updater');
          // For mobile, use Capacitor Updater
          await handleMobileUpdate(updateUrl, isMandatory, latestVersion);
        } else {
          log('Detected web platform, showing notification');
          // For web, just show a notification
          toast.info(
            `Update available (${latestVersion})! Refresh the page to get the latest version.`,
            { autoClose: false }
          );
        }
      }
    } catch (error: unknown) {
      logError('Error checking for updates:', error);
    } finally {
      setIsCheckingUpdate(false);
      log('Update check completed');
    }
  }, [
    appInfo, 
    hasCheckedThisSession, 
    isCheckingUpdate, 
    log, 
    logError,
    getCurrentBundle,
    handleMobileUpdate,
    shouldThrottleUpdateCheck,
    setHasCheckedThisSession,
    setIsCheckingUpdate,
    setUpdateAvailable
  ]);

  // Get app information on mount
  useEffect(() => {
    const getAppInfo = async () => {
      log('Getting app information...');
      try {
        log('Platform detection', { platform: Capacitor.getPlatform(), isNative: Capacitor.isNativePlatform() });
        
        // For mobile platforms, ALWAYS prioritize the bundle version if available
        if (Capacitor.isNativePlatform()) {
          log('Detected mobile platform, checking bundle info first...');
          
          // Try to get the current bundle info first
          try {
            const currentBundle = await getCurrentBundle();
            
            // If we have a valid bundle with a version, use that as our primary version
            if (currentBundle?.bundle?.version && 
                currentBundle.bundle.version !== 'builtin' && 
                currentBundle.bundle.status === 'success') {
              
              // Get device info for platform
              const deviceInfo = await Device.getInfo();
              const info = await CapacitorApp.getInfo();
              
              const bundleInfo = {
                version: currentBundle.bundle.version, // Use bundle version, not native version
                platform: deviceInfo.platform.toLowerCase(),
                appId: info.id
              };
              
              log('Using bundle version for app info:', JSON.stringify(bundleInfo));
              log('Native app info:', JSON.stringify(info));
              log('Device info:', JSON.stringify(deviceInfo));
              
              // Set app info using bundle version
              setAppInfo(bundleInfo);
              return;
            }
          } catch (bundleError: unknown) {
            logError('Error getting bundle info:', bundleError);
          }
          
          // Fallback to native app info if bundle info isn't available
          try {
            const info = await CapacitorApp.getInfo();
            const deviceInfo = await Device.getInfo();
            
            const mobileInfo = {
              version: info.version,
              platform: deviceInfo.platform.toLowerCase(),
              appId: info.id
            };
            
            log('Using native app info (no bundle):', JSON.stringify(mobileInfo));
            log('Full App.getInfo():', JSON.stringify(info));
            log('Full Device.getInfo():', JSON.stringify(deviceInfo));
            
            // Set app info
            setAppInfo(mobileInfo);
            return;
          } catch (deviceError: unknown) {
            logError('Error getting device info:', deviceError);
            // Continue to fallback
          }
        }

        // For web or if Capacitor APIs failed, use package.json version
        log('Using web platform or fallback');
        const webInfo = {
          version: packageJson.version || DEFAULT_VERSION,
          platform: Capacitor.getPlatform() === 'android' ? 'android' : 'web',
          appId: APP_ID
        };
        log('Setting web app info in state');
        setAppInfo(webInfo);
      } catch (error: unknown) {
        logError('Error getting app info:', error);
        // Fallback to defaults
        const fallbackInfo = {
          version: DEFAULT_VERSION,
          platform: Capacitor.getPlatform() === 'android' ? 'android' : 'web',
          appId: APP_ID
        };
        log('Setting emergency fallback app info in state');
        setAppInfo(fallbackInfo);
      }
    };

    // Execute immediately
    getAppInfo();
  }, [log, logError, getCurrentBundle]);


  // Reset the check flag when component unmounts
  useEffect(() => {
    return () => {
      log('Component unmounting, resetting state');
    };
  }, [log]);

  return {
    appInfo,
    isCheckingUpdate,
    updateAvailable,
    checkForUpdates,
    hasCheckedThisSession,
    clearUpdateThrottling
  };
};

export default useAppUpdater;